/*
  This content is mostly be copied over from MakePayment.tsx and still needs a cleanup/refactor itself
*/
import { useCallback, useState } from 'react';

import { CompanyFragment, PaymentMethod } from '@willow/graphql-iso/src/portal';
import { stripNullFromNullOrUndefined } from '@willow/shared-iso';
import {
  getActivePortalDelinquentInterestSetting,
  getUnfulfilledPaymentsDue,
  isGqlStatementBreakdown,
  NamedMemo,
} from '@willow/shared-web';

import { PortalSelectedLoan } from '../../../App';
import { getTotalAmountDue } from '../../../pages/make-payment/utils';
import { getFeesDueSorted } from '../../../utils/fees';
import { InterestReservePayment } from '../../interest-reserve-payment/InterestReservePayment';
import { PaymentForm } from '../../payment-form/PaymentForm';
import { PaidCurrentWithWorkoutPlan } from './PaidCurrentWithWorkoutPlan';
import { PaymentDetails } from './payment-details/PaymentDetails';
import { PaymentHeader } from './payment-header/PaymentHeader';
import { PaymentProcessing } from './PaymentProcessing';
import { PreviousPayment } from './previous-payment/PreviousPayment';

export interface Props {
  company: CompanyFragment;
  loan: PortalSelectedLoan;
  refetch: (poll?: boolean) => void;
}

export const PaymentsAccepted = NamedMemo<Props>('PaymentsAccepted', ({ company, loan, refetch }) => {
  const {
    id: loanId,
    currentSnapshot: {
      loanStatus,
      paymentDueStatus,
      paymentDueSubStatus,
      escrowShortageAnalysisId,
      shortageAmount,
      chargeAmountDue,
      paymentsDue,
      paymentToPaymentDue,
      payments,
      charges,
      openPayoffStatus,
      nextStatementDate,
      transferData,
      autopaySettings,
      stopSettings,
      delinquentInterestSettings,
      currentBillSettingsType,
      activeWorkoutPlan,
      snapshot: {
        balances: { escrowBalance },
      },
      totalAmountRemainingFromBorrower,
    },
    configuration: { hasInterestReserve },
  } = loan;

  const totalAmountDue = getTotalAmountDue(loan); // This amount accounts for active workout plans
  const totalAmountDueToReinstateLoan = getTotalAmountDue(loan, true); // actual amount due
  const exactPaymentOnly =
    stopSettings.portalPayments === 'exact' || Boolean(loan.company.adminSettings.exactPaymentsOnly);

  const borrowerHasAmountRemaining = totalAmountDue > 0;
  const hasPaymentDue = paymentDueStatus === 'due' && borrowerHasAmountRemaining;
  const hasInterestReserveDue = paymentDueStatus !== 'submitted' && !borrowerHasAmountRemaining && hasInterestReserve;

  const displayPaymentForm = hasPaymentDue;

  const displayPaymentProcessingSection =
    paymentDueStatus === 'submitted' && !borrowerHasAmountRemaining && !activeWorkoutPlan;
  const displayPreviousPaymentSection =
    paymentDueStatus === 'notDue' && !borrowerHasAmountRemaining && !activeWorkoutPlan;
  const displayActiveWorkoutPlanPaidCurrentSection = activeWorkoutPlan && !borrowerHasAmountRemaining;

  /*
      Future enhancement: just copying the below over from makepayment.tsx
      this stuff should be handled by paymentform
  */
  const [displayAdditionalPayment, setDisplayAdditionalPayment] = useState(false); // Allow additional payments to be made within certain statuses
  const unfulfilledCharges = getFeesDueSorted(charges);
  const activeDelinquentInterestSetting = getActivePortalDelinquentInterestSetting(delinquentInterestSettings);

  // Determine if there are processing payments
  let processingPayments: PortalSelectedLoan['currentSnapshot']['payments'] = [];
  let isOutstanding: boolean = false;
  if (
    (['due', 'submitted'].includes(paymentDueStatus) || borrowerHasAmountRemaining) &&
    paymentToPaymentDue.length > 0
  ) {
    // Check if any payments have been made already associated with any of the paymentsDue items
    const unfulfilledPayments = getUnfulfilledPaymentsDue(paymentsDue);
    const paymentDueIds = unfulfilledPayments?.map((unfulfilledPayement) => unfulfilledPayement.id);
    const associatedPaymentIds = paymentToPaymentDue
      .filter((ppd) => {
        return isGqlStatementBreakdown(ppd) && paymentDueIds?.includes(ppd.paymentDueId);
      })
      .map((item) => item.paymentId);

    if (payments) {
      // Find all associated payments for this payment due
      const newAssociatedPayments = associatedPaymentIds?.length
        ? payments.filter((paymentItem) => {
            return associatedPaymentIds.includes(paymentItem.id);
          })
        : [];

      // Determine if there is an outstanding balance status
      // Outstanding if payment is still due + there are already payments associated with it
      isOutstanding = paymentDueStatus === 'due' && newAssociatedPayments.length > 0;

      // Pick out processing payments for the current paymentDue
      // if "due" status: we are only interested in unfulfilled payments (the totalAmountRemainingFromBorrower > 0 if there are outstanding charges)
      // if "submitted" status, all payments will already be marked fulfilled, so look through all payments for processing status
      const statusProcessingPayments = paymentDueStatus === 'due' ? newAssociatedPayments : payments;
      processingPayments = statusProcessingPayments.filter((paymentItem) => {
        return paymentItem.status === 'PENDING' && paymentItem.method !== PaymentMethod.Reserve;
      });
    }
  }

  const handlePaymentSubmission = useCallback(() => {
    // After a payment has been successfully submitted, hide the form until the user requests it again
    setDisplayAdditionalPayment(false);
  }, []);

  return (
    <>
      <PaymentHeader loan={loan} />

      {displayPaymentForm && (
        <>
          <PaymentForm
            loanId={loanId}
            loanStatus={loanStatus}
            company={company}
            totalAmountDue={totalAmountDue}
            totalAmountDueToReinstateLoan={totalAmountDueToReinstateLoan}
            shortageAmount={shortageAmount}
            escrowShortageAnalysisId={escrowShortageAnalysisId}
            chargesAmountDue={chargeAmountDue}
            paymentsDue={paymentsDue}
            processingPayments={processingPayments}
            chargesDue={unfulfilledCharges}
            subStatus={paymentDueSubStatus}
            hasOutstandingBalance={isOutstanding}
            nextStatementDate={nextStatementDate}
            autopaySettings={autopaySettings}
            hasInterestReserve={loan.configuration.hasInterestReserve}
            openPayoffStatus={openPayoffStatus ?? undefined}
            exactPaymentOnly={exactPaymentOnly}
            refetch={refetch}
            activeDelinquentInterestSetting={activeDelinquentInterestSetting}
            currentBillSettingsType={currentBillSettingsType}
            activeWorkoutPlan={activeWorkoutPlan ?? undefined}
            applyPartialPayment={company.adminSettings.applyPartialPayments ?? false}
            repayAccounts={loan.repayAccounts}
            escrowBalance={escrowBalance}
          />
          {processingPayments?.length > 0 && (
            <PaymentDetails
              companyId={company.id}
              loanId={loanId}
              payments={processingPayments}
              status={paymentDueStatus}
              subStatus={paymentDueSubStatus}
              refetch={refetch}
            />
          )}
        </>
      )}

      {displayPaymentProcessingSection && (
        <PaymentProcessing
          companyId={company.id}
          loanId={loanId}
          processingPayments={processingPayments}
          status={paymentDueStatus}
          subStatus={paymentDueSubStatus}
          autopaySettings={autopaySettings ?? undefined}
          onAdditionalPayment={() => {
            setDisplayAdditionalPayment(true);
          }}
          refetch={refetch}
        />
      )}

      {displayPreviousPaymentSection && (
        <PreviousPayment
          paymentsDue={paymentsDue}
          paymentToPaymentDue={paymentToPaymentDue || []}
          paymentsMade={payments}
          onAdditionalPayment={() => {
            setDisplayAdditionalPayment(true);
          }}
          transferData={stripNullFromNullOrUndefined(transferData)}
        />
      )}

      {displayActiveWorkoutPlanPaidCurrentSection && (
        <PaidCurrentWithWorkoutPlan
          activeWorkoutPlan={activeWorkoutPlan}
          totalAmountDue={totalAmountRemainingFromBorrower}
          payments={payments}
          onAdditionalPayment={() => {
            setDisplayAdditionalPayment(true);
          }}
        />
      )}

      {hasInterestReserveDue && (
        <InterestReservePayment
          onAdditionalPayment={() => {
            setDisplayAdditionalPayment(true);
          }}
        />
      )}
      {displayAdditionalPayment && (
        <PaymentForm
          loanId={loanId}
          loanStatus={loanStatus}
          company={company}
          totalAmountDue={totalAmountDue}
          totalAmountDueToReinstateLoan={totalAmountDueToReinstateLoan}
          shortageAmount={shortageAmount}
          escrowShortageAnalysisId={escrowShortageAnalysisId}
          chargesAmountDue={chargeAmountDue}
          paymentsDue={paymentsDue || []}
          processingPayments={processingPayments}
          chargesDue={unfulfilledCharges}
          subStatus={paymentDueSubStatus}
          isAdditionalPayment
          nextStatementDate={nextStatementDate}
          autopaySettings={autopaySettings}
          hasInterestReserve={hasInterestReserve}
          openPayoffStatus={openPayoffStatus ?? undefined}
          exactPaymentOnly={exactPaymentOnly}
          onPaymentSubmission={handlePaymentSubmission}
          refetch={refetch}
          activeDelinquentInterestSetting={activeDelinquentInterestSetting}
          currentBillSettingsType={currentBillSettingsType}
          activeWorkoutPlan={activeWorkoutPlan ?? undefined}
          applyPartialPayment={company.adminSettings.applyPartialPayments ?? false}
          repayAccounts={loan.repayAccounts}
          escrowBalance={escrowBalance}
        />
      )}
    </>
  );
});
